/**
 * FormStateMachine - Clean state management for form lifecycle
 *
 * This replaces the complex boolean flag system with a predictable state machine.
 * No more preservation logic, safety timeouts, or ambiguous state combinations.
 */

export enum FormState {
    CLEAN = 'CLEAN', // Form has no changes
    DESIGN_DIRTY = 'DESIGN_DIRTY', // Only design section changes (Paved/Airport only - auto-submit, no redirect)
    FORM_DIRTY = 'FORM_DIRTY', // Non-design changes OR all changes for non-Paved/Airport projects (triggers redirect)
    SUBMITTING = 'SUBMITTING', // Form submission in progress
}

export enum FormEvent {
    DESIGN_CHANGE = 'DESIGN_CHANGE', // Design fields modified
    FORM_CHANGE = 'FORM_CHANGE', // Non-design fields modified
    SUBMIT_START = 'SUBMIT_START', // Form submission started
    SUBMIT_COMPLETE = 'SUBMIT_COMPLETE', // Form submission finished
    SUBMIT_FAILED = 'SUBMIT_FAILED', // ✅ CRITICAL FIX: Form submission failed (network error, API error, validation error)
    RESET = 'RESET', // Reset to clean state
}

export interface StateTransition {
    from: FormState;
    to: FormState;
    event: FormEvent;
    guard?: () => boolean;
}

export interface FormStateMachineState {
    current: FormState;
    previous: FormState | null;
    history: Array<{ state: FormState; event: FormEvent; timestamp: number }>;
    canRedirect: boolean;
    shouldAutoSubmit: boolean;
}

export class FormStateMachine {
    private state: FormStateMachineState;
    private listeners: Array<(state: FormStateMachineState) => void> = [];

    // Define valid state transitions
    private transitions: StateTransition[] = [
        // From CLEAN state
        { from: FormState.CLEAN, to: FormState.DESIGN_DIRTY, event: FormEvent.DESIGN_CHANGE },
        { from: FormState.CLEAN, to: FormState.FORM_DIRTY, event: FormEvent.FORM_CHANGE },
        { from: FormState.CLEAN, to: FormState.SUBMITTING, event: FormEvent.SUBMIT_START },

        // From DESIGN_DIRTY state
        { from: FormState.DESIGN_DIRTY, to: FormState.FORM_DIRTY, event: FormEvent.FORM_CHANGE },
        { from: FormState.DESIGN_DIRTY, to: FormState.SUBMITTING, event: FormEvent.SUBMIT_START },
        { from: FormState.DESIGN_DIRTY, to: FormState.CLEAN, event: FormEvent.RESET },

        // From FORM_DIRTY state
        // 🔧 FIX: Allow transition back to DESIGN_DIRTY when user makes design-only changes from FORM_DIRTY state
        // Example: User is on results page (FORM_DIRTY), clicks "Yes" on enableSurfaceDesign → should transition to DESIGN_DIRTY
        { from: FormState.FORM_DIRTY, to: FormState.DESIGN_DIRTY, event: FormEvent.DESIGN_CHANGE },
        { from: FormState.FORM_DIRTY, to: FormState.SUBMITTING, event: FormEvent.SUBMIT_START },
        { from: FormState.FORM_DIRTY, to: FormState.CLEAN, event: FormEvent.RESET },

        // From SUBMITTING state
        { from: FormState.SUBMITTING, to: FormState.CLEAN, event: FormEvent.SUBMIT_COMPLETE },
        // ✅ CRITICAL FIX: Recovery transitions when submission fails
        { from: FormState.SUBMITTING, to: FormState.FORM_DIRTY, event: FormEvent.SUBMIT_FAILED },
    ];

    constructor() {
        this.state = {
            current: FormState.CLEAN,
            previous: null,
            history: [],
            canRedirect: false,
            shouldAutoSubmit: false,
        };
    }

    public getCurrentState(): FormStateMachineState {
        return { ...this.state };
    }

    public transition(event: FormEvent): boolean {
        const validTransition = this.transitions.find(t => t.from === this.state.current && t.event === event);

        if (!validTransition) {
            console.warn(`❌ Invalid transition: ${this.state.current} + ${event}`);
            return false;
        }

        // Check guard condition if present
        if (validTransition.guard && !validTransition.guard()) {
            console.warn(`🚫 Transition blocked by guard: ${this.state.current} + ${event}`);
            return false;
        }

        // Execute transition
        const previousState = this.state.current;
        this.state.previous = previousState;
        this.state.current = validTransition.to;

        // Update derived properties
        this.updateDerivedProperties();

        // Add to history
        this.state.history.push({
            state: validTransition.to,
            event,
            timestamp: Date.now(),
        });

        // Keep history manageable
        if (this.state.history.length > 50) {
            this.state.history = this.state.history.slice(-25);
        }

        console.log(`✅ State transition: ${previousState} → ${this.state.current} (${event})`);

        // Notify listeners
        this.notifyListeners();

        return true;
    }

    private updateDerivedProperties(): void {
        // Set canRedirect: only FORM_DIRTY state should trigger redirect
        this.state.canRedirect = this.state.current === FormState.FORM_DIRTY;

        // Set shouldAutoSubmit: DESIGN_DIRTY state should auto-submit for recalculation
        this.state.shouldAutoSubmit = this.state.current === FormState.DESIGN_DIRTY;
    }

    public reset(): boolean {
        return this.transition(FormEvent.RESET);
    }

    public startSubmission(): boolean {
        return this.transition(FormEvent.SUBMIT_START);
    }

    public completeSubmission(): boolean {
        return this.transition(FormEvent.SUBMIT_COMPLETE);
    }

    public handleFormChange(isDesignOnly: boolean): boolean {
        const event = isDesignOnly ? FormEvent.DESIGN_CHANGE : FormEvent.FORM_CHANGE;
        return this.transition(event);
    }

    // State query methods
    public isClean(): boolean {
        return this.state.current === FormState.CLEAN;
    }

    public isDesignDirty(): boolean {
        return this.state.current === FormState.DESIGN_DIRTY;
    }

    public isFormDirty(): boolean {
        return this.state.current === FormState.FORM_DIRTY;
    }

    public isSubmitting(): boolean {
        return this.state.current === FormState.SUBMITTING;
    }

    public canRedirect(): boolean {
        return this.state.canRedirect;
    }

    public shouldAutoSubmit(): boolean {
        return this.state.shouldAutoSubmit;
    }

    // Listener management
    public subscribe(listener: (state: FormStateMachineState) => void): () => void {
        this.listeners.push(listener);
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => {
            try {
                listener({ ...this.state });
            } catch (error) {
                console.error('Error in FormStateMachine listener:', error);
            }
        });
    }

    // Debug helpers
    public getValidTransitions(): FormEvent[] {
        return this.transitions.filter(t => t.from === this.state.current).map(t => t.event);
    }

    public getStateHistory(): Array<{ state: FormState; event: FormEvent; timestamp: number }> {
        return [...this.state.history];
    }

    public exportState(): string {
        return JSON.stringify(
            {
                current: this.state.current,
                previous: this.state.previous,
                canRedirect: this.state.canRedirect,
                shouldAutoSubmit: this.state.shouldAutoSubmit,
                validTransitions: this.getValidTransitions(),
                historyLength: this.state.history.length,
            },
            null,
            2
        );
    }
}
